import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { ExportFormat } from '@markbird/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { getDocument, renameDocument, setPinned, setDocFolder } from '../lib/library';
import { listDocumentMeta } from '../lib/docMeta';
import type { DocMeta } from '../lib/db';
import { highlightSegments } from '../lib/search';
import { searchLibrary } from '../lib/searchClient';
import { highlightTerms, parseQuery, tagMatches } from '../lib/searchQuery';
import { allTags } from '../lib/tags';
import { allFolders, groupByFolder, visibleFolders, normalizeFolderPath } from '../lib/folders';
import { formatRelative, formatDateTime } from '../lib/format';
import { askPrompt } from '../lib/dialogs';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useStore, SIDEBAR_MIN, SIDEBAR_MAX, SIDEBAR_DEFAULT } from '../store/useStore';
import { useT } from '../i18n/useT';
import { useDocDrag, dropRowProps, dropTargetProps, type DropTarget } from '../hooks/useDocDrag';
import { moveByStep, placeNextTo } from '../lib/manualOrder';
import { useIncrementalLimit, useIncrementalList } from '../hooks/useIncrementalList';
import { Icon } from './Icon';
import { Menu, MenuItem, MenuSeparator, anchorBelow, anchorAtPointer } from './Menu';
import type { MenuAnchor } from './Menu';

/** Stable empty list, so the search effect does not re-run on every render. */
const NO_DOCS: DocMeta[] = [];

/** One row of the result list. */
interface Hit {
  doc: DocMeta;
  snippet: string | null;
}

/**
 * Search results for the current query, restricted to `pool` (the tag filter).
 *
 * The query runs against the in-memory index rather than over document text, so
 * it stays cheap as the library grows; an empty query is not a search at all and
 * simply shows the pool.
 */
function useSearchHits(query: string, pool: DocMeta[]): Hit[] {
  const [hits, setHits] = useState<Hit[]>([]);

  useEffect(() => {
    if (!query.trim()) {
      setHits(pool.map((doc) => ({ doc, snippet: null })));
      return;
    }
    let cancelled = false;
    void searchLibrary(query).then((results) => {
      if (cancelled) return;
      const byId = new Map(pool.map((d) => [d.id, d]));
      setHits(
        results.flatMap((h) => {
          const doc = byId.get(h.id);
          return doc ? [{ doc, snippet: h.snippet }] : [];
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [query, pool]);

  return hits;
}

/** Render text with query matches wrapped in <mark>. */
function Highlighted({ text, tokens }: { text: string; tokens: string[] }) {
  if (tokens.length === 0) return <>{text}</>;
  return (
    <>
      {highlightSegments(text, tokens).map((seg, i) =>
        seg.match ? <mark key={i}>{seg.text}</mark> : <span key={i}>{seg.text}</span>,
      )}
    </>
  );
}

/**
 * Document library sidebar, backed by the Dexie library via `useLiveQuery` so
 * the list reflects writes (create/rename/delete/autosave) instantly.
 */
const EXPORT_FORMATS: { fmt: ExportFormat; key: string }[] = [
  { fmt: 'md', key: 'export.md' },
  { fmt: 'html', key: 'export.html' },
  { fmt: 'pdf', key: 'export.pdf' },
  { fmt: 'docx', key: 'export.docx' },
  { fmt: 'slides', key: 'export.slides' },
];

export function Sidebar({
  drawer = false,
  onClose,
}: { drawer?: boolean; onClose?: () => void } = {}) {
  const t = useT();
  const controller = useDocControllerContext();
  const activeId = useStore((s) => s.activeDoc.id);
  const [query, setQuery] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [rowMenu, setRowMenu] = useState<{ id: string; anchor: MenuAnchor } | null>(null);
  const [newMenu, setNewMenu] = useState<MenuAnchor | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const tagFilter = useStore((s) => s.tagFilter);
  const setTagFilter = useStore((s) => s.setTagFilter);
  const sortBy = useStore((s) => s.sortBy);
  const setSortBy = useStore((s) => s.setSortBy);
  const collapsedFolders = useStore((s) => s.collapsedFolders);
  const toggleFolder = useStore((s) => s.toggleFolder);
  const sidebarWidth = useStore((s) => s.sidebarWidth);
  const setSidebarWidth = useStore((s) => s.setSidebarWidth);
  // `undefined` while the query is still resolving → distinguishes the initial
  // load from a genuinely empty library (so "No documents yet." doesn't flash
  // before the welcome document is seeded).
  // Metadata only: the library list never needs document bodies, and reading
  // them would pull the whole vault into memory on every autosave.
  const docsRaw = useLiveQuery(() => listDocumentMeta(sortBy), [sortBy]);
  const loading = docsRaw === undefined;
  const docs = useMemo(() => docsRaw ?? NO_DOCS, [docsRaw]);

  // Drawer mode (narrow screens): focus search on open, close on Escape.
  useEffect(() => {
    if (!drawer) return;
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer, onClose]);

  const tags = useMemo(() => allTags(docs), [docs]);
  // A tag filter also selects the tags nested under it, so `work` finds
  // `work/urgent` the way the `tag:` search operator does.
  const byTag = useMemo(
    () =>
      tagFilter
        ? docs.filter((d) => d.tags.some((t) => tagMatches(tagFilter.toLowerCase(), t)))
        : docs,
    [docs, tagFilter],
  );
  const tokens = useMemo(() => highlightTerms(parseQuery(query)), [query]);
  const hits = useSearchHits(query, byTag);
  // A vault-sized result list would otherwise mount thousands of rows on every
  // re-render; pages are appended as the bottom comes into view.
  const { visible: visibleHits, hasMore, sentinelRef, loadMore } = useIncrementalList(hits);

  const commitRename = async (id: string) => {
    await renameDocument(id, renameValue);
    if (id === activeId) useStore.getState().setTitle(renameValue.trim() || 'Untitled');
    setRenamingId(null);
  };

  // Folder tree is shown only in the unfiltered view; search/tag results stay flat.
  const showTree = !query.trim() && !tagFilter;
  const folders = useMemo(() => (showTree ? allFolders(docs) : []), [docs, showTree]);
  const grouped = useMemo(() => groupByFolder(docs), [docs]);
  const collapsedSet = useMemo(() => new Set(collapsedFolders), [collapsedFolders]);
  const rootDocs = useMemo(() => grouped.get(null) ?? NO_DOCS, [grouped]);

  // The tree is paged too, across its groups: a 5,000-note library used to mount
  // every row at startup, one long main-thread task before the app answered.
  // The limit resets only when the order changes, not on every autosave, so a
  // list scrolled far down stays where it was while you write.
  const treePage = useIncrementalLimit(sortBy);
  const tree = useMemo(() => {
    if (!showTree) return null;
    let budget = treePage.limit;
    let total = rootDocs.length;
    const groups = visibleFolders(folders, collapsedSet).map((folder) => {
      const all = folder.collapsed ? [] : (grouped.get(folder.path) ?? []);
      total += all.length;
      const shown = all.slice(0, Math.max(0, budget));
      budget -= shown.length;
      return { folder, docs: shown };
    });
    const root = rootDocs.slice(0, Math.max(0, budget));
    return { groups, root, hasMore: total > treePage.limit };
  }, [showTree, treePage.limit, rootDocs, folders, collapsedSet, grouped]);
  // Rows can be reordered only where the order on screen is the manual order:
  // search and tag results are ranked by relevance, and other sorts are derived.
  const reorderable = showTree && sortBy === 'manual';
  const docsRef = useRef(docs);
  docsRef.current = docs;

  // After a keyboard move the row is re-rendered in its new place; keep focus on
  // it so Alt+Arrow can be pressed again.
  const [refocusId, setRefocusId] = useState<string | null>(null);
  useEffect(() => {
    if (!refocusId) return;
    document
      .querySelector<HTMLElement>(`[data-drop-doc="${CSS.escape(refocusId)}"] .mb-docitem-open`)
      ?.focus();
  }, [docs, refocusId]);

  const moveStep = async (id: string, delta: -1 | 1) => {
    setRefocusId(id);
    await moveByStep(docsRef.current, id, delta);
  };

  // Drag-to-resize, mirroring the editor/preview splitter. The drawer is a
  // full-width overlay, so it keeps its own sizing.
  const startResize = useCallback(() => {
    if (drawer) return;
    const onMove = (e: MouseEvent) => setSidebarWidth(e.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [drawer, setSidebarWidth]);

  // The handle is a `role="separator"`, so it has to answer to the keyboard
  // too — a mouse-only separator is an empty promise to keyboard users.
  const onResizeKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const STEP = e.shiftKey ? 40 : 12;
    if (e.key === 'ArrowLeft') setSidebarWidth(sidebarWidth - STEP);
    else if (e.key === 'ArrowRight') setSidebarWidth(sidebarWidth + STEP);
    else if (e.key === 'Home') setSidebarWidth(SIDEBAR_MIN);
    else if (e.key === 'End') setSidebarWidth(SIDEBAR_MAX);
    else if (e.key === 'Enter' || e.key === ' ') setSidebarWidth(SIDEBAR_DEFAULT);
    else return;
    e.preventDefault();
  };

  // Pointer-based drag & drop, so moving a document into a folder also works
  // on touch (HTML5 DnD never fires there).
  const { draggingId, dropTarget, rowTarget, rowProps } = useDocDrag(
    useCallback((id: string, target: DropTarget) => {
      if (target.kind === 'folder') void setDocFolder(id, normalizeFolderPath(target.folder));
      else void placeNextTo(docsRef.current, id, target.id, target.after);
    }, []),
  );

  const dropProps = (folder: string | null) => ({
    ...dropTargetProps(folder),
    'data-drop-active': dropTarget === (folder ?? '') ? '' : undefined,
  });

  const renderRow = (doc: DocMeta, snippet?: string | null, depth = 0) => (
    <li
      key={doc.id}
      className={`mb-docitem ${doc.id === activeId ? 'active' : ''} ${doc.pinned ? 'pinned' : ''}`}
      {...(reorderable ? dropRowProps(doc.id) : {})}
      data-drop-position={
        rowTarget?.id === doc.id ? (rowTarget.after ? 'after' : 'before') : undefined
      }
      style={depth ? { paddingLeft: `${depth * 0.9}rem` } : undefined}
      // Right-click anywhere on the row opens the same menu as the "⋯" button.
      onContextMenu={(e) => {
        if (renamingId === doc.id) return; // let the input keep its native menu
        e.preventDefault();
        setRowMenu({ id: doc.id, anchor: anchorAtPointer(e) });
      }}
      {...(renamingId === doc.id ? {} : rowProps(doc.id))}
    >
      {renamingId === doc.id ? (
        <input
          className="mb-rename-input"
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={() => void commitRename(doc.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commitRename(doc.id);
            if (e.key === 'Escape') setRenamingId(null);
          }}
        />
      ) : (
        <button
          className="mb-docitem-open"
          // Tells assistive tech which document is currently open — `.active`
          // was purely visual before.
          aria-current={doc.id === activeId ? 'true' : undefined}
          aria-keyshortcuts={reorderable ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
          onKeyDown={(e) => {
            if (!reorderable || !e.altKey) return;
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            e.preventDefault();
            void moveStep(doc.id, e.key === 'ArrowUp' ? -1 : 1);
          }}
          onClick={() => {
            void controller.open(doc.id);
            if (drawer) onClose?.();
          }}
        >
          <span className="mb-docitem-title">
            {doc.title ? <Highlighted text={doc.title} tokens={tokens} /> : t('common.untitled')}
          </span>
          {snippet && (
            <span className="mb-docitem-snippet">
              <Highlighted text={snippet} tokens={tokens} />
            </span>
          )}
          <span className="mb-docitem-date" title={formatDateTime(doc.updatedAt)}>
            {formatRelative(doc.updatedAt)}
          </span>
          {doc.tags.length > 0 && (
            <span className="mb-docitem-tags">
              {doc.tags.slice(0, 4).map((tag) => (
                <span key={tag} className="mb-tag-chip mb-tag-chip--mini">
                  {tag}
                </span>
              ))}
            </span>
          )}
        </button>
      )}
      <div className="mb-docitem-actions">
        <button
          title={t('tags.edit')}
          aria-label={t('tags.edit')}
          onClick={() => useStore.getState().setTagsEditorFor(doc.id)}
        >
          <Icon name="tag" />
        </button>
        <button
          title={doc.pinned ? t('sidebar.unpin') : t('sidebar.pin')}
          aria-label={doc.pinned ? t('sidebar.unpin') : t('sidebar.pin')}
          aria-pressed={!!doc.pinned}
          className={doc.pinned ? 'mb-pin-active' : ''}
          onClick={() => void setPinned(doc.id, !doc.pinned)}
        >
          <Icon name={doc.pinned ? 'star-filled' : 'star'} />
        </button>
        <button
          title={t('sidebar.more')}
          aria-label={t('sidebar.more')}
          aria-haspopup="menu"
          aria-expanded={rowMenu?.id === doc.id}
          onClick={(e) => {
            // Measure now: `currentTarget` is null by the time a functional
            // updater runs, and the anchor has to come from this button.
            const at = anchorBelow(e.currentTarget);
            setRowMenu((m) => (m?.id === doc.id ? null : { id: doc.id, anchor: at }));
          }}
        >
          <Icon name="more-horizontal" />
        </button>
      </div>
    </li>
  );

  const menuDoc = rowMenu ? docs.find((d) => d.id === rowMenu.id) : undefined;

  return (
    <aside
      className={`mb-sidebar ${drawer ? 'mb-sidebar--drawer' : ''}`}
      style={drawer ? undefined : { width: sidebarWidth }}
      aria-label="Documents"
      role={drawer ? 'dialog' : undefined}
      aria-modal={drawer ? true : undefined}
    >
      <div className="mb-sidebar-head">
        <div className="mb-search-wrap">
          <Icon name="search" className="mb-search-icon" />
          <input
            ref={searchRef}
            className="mb-search"
            placeholder={t('sidebar.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('sidebar.searchAria')}
          />
        </div>
        <div className="mb-new-split">
          <button
            className="mb-btn mb-btn-primary"
            onClick={() => void controller.create()}
            title={t('sidebar.new')}
            aria-label={t('sidebar.new')}
          >
            <Icon name="plus" />
          </button>
          <button
            className="mb-btn mb-btn-primary mb-new-split-more"
            onClick={(e) => {
              const at = anchorBelow(e.currentTarget);
              setNewMenu((m) => (m ? null : at));
            }}
            title={t('sidebar.newMore')}
            aria-label={t('sidebar.newMore')}
            aria-haspopup="menu"
            aria-expanded={!!newMenu}
          >
            <Icon name="chevron-down" />
          </button>
        </div>
        {drawer && (
          <button
            className="mb-btn mb-drawer-close"
            onClick={onClose}
            title={t('common.close')}
            aria-label={t('common.close')}
          >
            <Icon name="x" />
          </button>
        )}
      </div>

      {tags.length > 0 && (
        <div className="mb-tagbar" role="group" aria-label={t('tags.filterAria')}>
          <button
            className={`mb-tag-chip ${tagFilter === null ? 'active' : ''}`}
            onClick={() => setTagFilter(null)}
          >
            {t('tags.all')}
          </button>
          {tags.map((tag) => (
            <button
              key={tag}
              className={`mb-tag-chip ${tagFilter && tagFilter.toLowerCase() === tag.toLowerCase() ? 'active' : ''}`}
              onClick={() =>
                setTagFilter(tagFilter?.toLowerCase() === tag.toLowerCase() ? null : tag)
              }
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <div className="mb-sortbar">
        <label className="mb-sort-label">
          <span className="mb-sort-caption">{t('sidebar.sort')}</span>
          <select
            className="mb-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            aria-label={t('sidebar.sort')}
          >
            <option value="updated">{t('sidebar.sortUpdated')}</option>
            <option value="created">{t('sidebar.sortCreated')}</option>
            <option value="title">{t('sidebar.sortTitle')}</option>
            <option value="manual">{t('sidebar.sortManual')}</option>
          </select>
        </label>
      </div>

      <ul className="mb-doclist">
        {tree
          ? tree.groups.map(({ folder: f, docs: folderDocs }) => {
              return (
                <li key={`folder:${f.path}`} className="mb-folder-group">
                  <div
                    className="mb-folder-head"
                    style={{ paddingLeft: `${0.3 + f.depth * 0.9}rem` }}
                    {...dropProps(f.path)}
                  >
                    <button
                      className="mb-folder-toggle"
                      aria-expanded={!f.collapsed}
                      onClick={() => toggleFolder(f.path)}
                      title={f.name}
                    >
                      <Icon
                        name={f.collapsed ? 'chevron-right' : 'chevron-down'}
                        className="mb-folder-caret"
                        size={14}
                      />
                      <span className="mb-folder-name">{f.name}</span>
                    </button>
                    <button
                      className="mb-folder-action"
                      title={t('folder.rename')}
                      aria-label={t('folder.rename')}
                      onClick={async () => {
                        const next = await askPrompt({
                          title: t('folder.renamePrompt'),
                          initial: f.name,
                          confirmLabel: t('folder.rename'),
                        });
                        if (!next) return;
                        const parent = f.path.includes('/')
                          ? f.path.slice(0, f.path.lastIndexOf('/') + 1)
                          : '';
                        const { renameFolder } = await import('../lib/library');
                        await renameFolder(f.path, `${parent}${next}`);
                      }}
                    >
                      <Icon name="pencil" size={14} />
                    </button>
                  </div>
                  {folderDocs.map((d) => renderRow(d, undefined, f.depth + 1))}
                </li>
              );
            })
          : visibleHits.map(({ doc, snippet }) => renderRow(doc, snippet))}
        {!showTree && hasMore && (
          <li ref={sentinelRef as (node: HTMLLIElement | null) => void}>
            {/* The observer normally loads the next page on scroll; the button
                is the keyboard-reachable equivalent. */}
            <button className="mb-showmore" onClick={loadMore}>
              {t('sidebar.showMore')}
            </button>
          </li>
        )}
        {tree?.root.map((d) => renderRow(d))}
        {tree?.hasMore && (
          <li ref={treePage.sentinelRef as (node: HTMLLIElement | null) => void}>
            <button className="mb-showmore" onClick={treePage.loadMore}>
              {t('sidebar.showMore')}
            </button>
          </li>
        )}
        {showTree && draggingId && (
          // Only present mid-drag: a strip that moves a document back out of
          // its folder to the library root.
          <li className="mb-root-drop" {...dropProps(null)}>
            {t('folder.dropToRoot')}
          </li>
        )}
        {hits.length === 0 &&
          (loading ? (
            <li className="mb-doclist-empty mb-doclist-loading">
              <p>{t('sidebar.loading')}</p>
            </li>
          ) : (
            <li className="mb-doclist-empty">
              <img src="/illustrations/empty-library.svg" alt="" width="140" height="79" />
              <p>{query.trim() ? t('sidebar.emptyNoMatch') : t('sidebar.empty')}</p>
            </li>
          ))}
      </ul>

      {!drawer && (
        <div
          className="mb-sidebar-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('sidebar.resize')}
          aria-valuenow={sidebarWidth}
          aria-valuemin={SIDEBAR_MIN}
          aria-valuemax={SIDEBAR_MAX}
          tabIndex={0}
          title={t('sidebar.resize')}
          onMouseDown={startResize}
          onKeyDown={onResizeKey}
          onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
        />
      )}

      {newMenu && (
        <Menu anchor={newMenu} onClose={() => setNewMenu(null)} label={t('sidebar.newMore')}>
          <MenuItem onSelect={() => useStore.getState().setTemplatesOpen(true)}>
            <Icon name="folder" size={15} />
            {t('sidebar.newTemplate')}
          </MenuItem>
          <MenuItem onSelect={() => useStore.getState().setImportOpen(true)}>
            <Icon name="upload" size={15} />
            {t('sidebar.upload')}
          </MenuItem>
          <MenuItem onSelect={() => useStore.getState().setImportUrlOpen(true)}>
            <Icon name="link" size={15} />
            {t('sidebar.importUrl')}
          </MenuItem>
        </Menu>
      )}

      {rowMenu && menuDoc && (
        <Menu
          anchor={rowMenu.anchor}
          onClose={() => setRowMenu(null)}
          label={menuDoc.title || t('common.untitled')}
          className="mb-row-menu"
        >
          <MenuItem
            onSelect={() => {
              setRenamingId(menuDoc.id);
              setRenameValue(menuDoc.title);
            }}
          >
            <Icon name="pencil" size={15} />
            {t('sidebar.rename')}
          </MenuItem>
          <MenuItem onSelect={() => void controller.duplicate(menuDoc.id)}>
            <Icon name="copy" size={15} />
            {t('sidebar.duplicate')}
          </MenuItem>
          <MenuItem onSelect={() => useStore.getState().setMoveToFolderFor(menuDoc.id)}>
            <Icon name="folder-input" size={15} />
            {t('folder.move')}
          </MenuItem>
          {reorderable && (
            <>
              <MenuItem onSelect={() => void moveStep(menuDoc.id, -1)}>
                <Icon name="arrow-up" size={15} />
                {t('sidebar.moveUp')}
              </MenuItem>
              <MenuItem onSelect={() => void moveStep(menuDoc.id, 1)}>
                <Icon name="arrow-down" size={15} />
                {t('sidebar.moveDown')}
              </MenuItem>
            </>
          )}
          <MenuSeparator />
          {EXPORT_FORMATS.map((e) => (
            <MenuItem
              key={e.fmt}
              onSelect={() =>
                // The row menu works from metadata, so the body is fetched only
                // when an export actually asks for it.
                void (async () => {
                  const doc = await getDocument(menuDoc.id);
                  if (!doc) return;
                  const m = await import('../lib/export');
                  await m.exportDocument(doc, e.fmt);
                })()
              }
            >
              <Icon name="download" size={15} />
              {t(e.key)}
            </MenuItem>
          ))}
          <MenuSeparator />
          {/* No confirmation: deleting is a restorable soft-delete and raises an
              undo toast, so a blocking prompt would guard a reversible action. */}
          <MenuItem danger onSelect={() => void controller.remove(menuDoc.id)}>
            <Icon name="trash" size={15} />
            {t('sidebar.delete')}
          </MenuItem>
        </Menu>
      )}
    </aside>
  );
}
